import Cookies from 'js-cookie';

import type { Style } from '../component/elements/StyleJigsaw';

import { translations } from './translations';
import type { VoiceDemoDataType } from './types';

export const getRandomText = (
  languageCode: keyof typeof translations,
  idx?: number,
): string => {
  const sampleParagraphs = translations[languageCode] ?? translations.en;
  return sampleParagraphs[
    // Sonar Ignore: Using Math.random() here is safe because cryptographic security is not required.
    Math.floor(idx ?? Math.random() * sampleParagraphs.length)
  ];
};

interface SSMLOptions {
  emotion?: string;
  cadence?: number;
  pitch?: number;
  speed?: number;
}

const escapeSSMLChars = (text: string) =>
  text.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');

export const textToSSML = (text: string, options: SSMLOptions = {}) => {
  const { emotion, cadence, pitch, speed } = options;

  let ssml = escapeSSMLChars(text);

  const emotionAttributes = [
    emotion ? `emotion="${emotion}"` : '',
    cadence ? `cadence="${cadence.toFixed(0)}%"` : '',
  ].filter(Boolean);
  if (emotionAttributes.length) {
    ssml = `<speechify:emotion ${emotionAttributes.join(
      ' ',
    )}>${ssml}</speechify:emotion>`;
  }

  const prosodyAttributes = [
    pitch ? `pitch="${pitch.toFixed(0)}%"` : '',
    speed ? `rate="${speed.toFixed(0)}%"` : '',
  ].filter(Boolean);
  if (prosodyAttributes.length) {
    ssml = `<prosody ${prosodyAttributes.join(' ')}>${ssml}</prosody>`;
  }

  return `<speak>${ssml}</speak>`;
};

export async function generateSHA256(input: string) {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

export async function getStaticAudioFileName(
  voiceId: string,
  locale: string,
  style: string,
  text: string,
): Promise<string> {
  const textHash = await generateSHA256(text);
  return `${voiceId}_${locale}_${style}_${textHash}.mp3`;
}

export const getEmotionFromStyle = (style?: Style | null) => {
  if (!style) return 'bright';
  if (style === 'Podcast') return 'warm';
  else if (style === 'Training') return 'cheerful';
  else return 'assertive';
};

export const isAcceptedVoiceStylePair = (voiceName: string, style: string) => {
  if (style === 'Youtube' && voiceName === 'Kristy') {
    return true;
  }
  if (style === 'Podcast' && voiceName === 'Carly') {
    return true;
  }
  if (style === 'Training' && voiceName === 'Oliver') {
    return true;
  }
  return false;
};

export const getVoicePreferenceFromStyle = (
  voices: VoiceDemoDataType['voices'],
  style: string,
) => {
  const selectedVoice = voices.find((voice) => {
    if (isAcceptedVoiceStylePair(voice.displayName, style)) {
      return true;
    }
  });
  return selectedVoice;
};

export const getDefaultVoice = (voices: VoiceDemoDataType['voices']) => {
  return voices.find((voice) => {
    if (voice.displayName === 'Oliver') return true;
  });
};

export const getCountryNameFromLanguageCode = (languageCode: string) => {
  try {
    const regionCode = languageCode.split('-')[1];
    if (!regionCode) return null;

    const regionName = new Intl.DisplayNames(['en'], { type: 'region' });
    return regionName.of(regionCode);
  } catch (error) {
    console.error('Error getting country name:', error);
    return null;
  }
};

export const countLanguages = (languageCodes: string[]) => {
  const languageCount: Record<string, number> = {};

  languageCodes.forEach((code) => {
    const languagePrefix = code.split('-')[0];
    if (languageCount[languagePrefix]) {
      languageCount[languagePrefix]++;
    } else {
      languageCount[languagePrefix] = 1;
    }
  });

  return languageCount;
};

export const getRandomNumberInRange = (min: number, max: number) => {
  if (min > max) {
    throw new Error('Min value cannot be greater than max value');
  }
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

interface JWTPayload {
  exp: number;
  iat?: number;
  [key: string]: unknown;
}

export const decodeJWT = (token: string): JWTPayload => {
  const payloadBase64 = token.split('.')[1];
  if (!payloadBase64) {
    throw new Error('Invalid JWT token format');
  }
  const payload = JSON.parse(atob(payloadBase64)) as JWTPayload;
  return payload;
};

export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = decodeJWT(token);
    if (!payload.exp) {
      return false; // If no expiration, consider it valid
    }
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  } catch {
    // If token is malformed, consider it expired
    return true;
  }
};

export const SM_BREAKPOINT = 640;

/**
 * Sanitizes text to prevent XSS attacks
 */
const sanitizeHtml = (text: string): string => {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
};

/**
 * Highlights emotion brackets in text by wrapping them in styled spans
 * Sanitizes content to prevent XSS attacks
 * @param text - The text containing emotion brackets like [excited]
 * @returns HTML string with highlighted emotion brackets
 */
export const highlightEmotionBrackets = (text: string): string => {
  return text.replace(/\[([^\]]+)\]/g, (match, content) => {
    const sanitized = sanitizeHtml(content);
    return `<span style="color: rgb(147, 51, 234); font-weight: 600;">[${sanitized}]</span>`;
  });
};

export const getUserIdOrAnonymousId = async (): Promise<string> => {
  const userId = Cookies.get('ajs_user_id');
  if (userId && userId.trim() !== 'null') {
    return userId;
  }
  const anonymousId = Cookies.get('ajs_anonymous_id');
  if (anonymousId && anonymousId.trim() !== 'null') {
    return anonymousId;
  } else if (window.analytics.instance) {
    return window.analytics.instance.user().anonymousId();
  } else {
    console.warn('analytics not found');
    throw new Error('analytics not found');
  }
};

export function carryParams(
  baseUrl: string,
  extra?: Record<string, string | number>,
): string {
  if (typeof window === 'undefined') return baseUrl;

  const url = new URL(baseUrl, window.location.origin);

  const existingParams = new URLSearchParams(window.location.search);
  existingParams.forEach((value, key) => url.searchParams.set(key, value));

  if (extra) {
    Object.entries(extra).forEach(([key, val]) =>
      url.searchParams.set(key, val.toString()),
    );
  }

  return url.toString();
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
export const retryWithDelay = async <T>(
  fn: () => Promise<T>,
  numRetries: number,
  delayTime: number,
  initialDelay: number,
): Promise<T> => {
  for (let retriesLeft = numRetries; retriesLeft > 0; retriesLeft--) {
    try {
      await delay(retriesLeft === numRetries ? initialDelay : delayTime);
      return await fn();
    } catch (e) {
      if (retriesLeft === 0) throw new Error('Failed to fetch details');
    }
  }
  throw new Error('Failed to fetch details');
};


  /**
   * Generic debounce wrapper function
   * @param fn - Function to debounce
   * @param delay - Delay in milliseconds
   * @returns Debounced function
   */
  export function debounce<T extends (...args: any[]) => any>(
    fn: T,
    delay: number
  ): (...args: Parameters<T>) => Promise<ReturnType<T> extends Promise<infer U> ? U : ReturnType<T>> {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    return (...args: Parameters<T>): Promise<ReturnType<T> extends Promise<infer U> ? U : ReturnType<T>> => {
      return new Promise((resolve) => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        timeoutId = setTimeout(async () => {
          const result = await fn(...args);
          resolve(result as ReturnType<T> extends Promise<infer U> ? U : ReturnType<T>);
          timeoutId = null;
        }, delay);
      });
    };
  }